/* Ricerca giocatori (port React del modulo playtomic-web) — tema chiaro Control Center.
   Ricerca club (nome/email/telefono) o globale, profilo completo, grafico livello
   interattivo, storico partite con "carica altre". */
import { useEffect, useMemo, useRef, useState } from "react";
import { ptapp, type Account } from "@/lib/ptapp";

type Mode = "club" | "global";
type PlayerLite = { id: string; name: string; pic?: string | null; gender?: string; email?: string; phone?: string };

const T = ptapp.MOVING_TENANT;
const esc = (s: unknown) => (s == null ? "" : String(s));
const genderLabel = (g?: string) => { const u = (g || "").toUpperCase(); return u === "MALE" ? "Uomo" : u === "FEMALE" ? "Donna" : (g || "—"); };
const fmtLevel = (v: number | null | undefined) => (v == null ? null : "Liv " + Number(v).toFixed(2).replace(".", ","));
const fmtL = (v: number) => Number(v).toFixed(2).replace(".", ",");
const ageOf = (bd?: string) => { if (!bd) return null; const y = new Date(bd).getFullYear(); if (isNaN(y)) return null; const a = new Date().getFullYear() - y; return a > 0 && a < 120 ? a : null; };
const fmtDay = (s?: string) => { if (!s) return ""; const d = new Date(s); return isNaN(+d) ? "" : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); };
const fmtDT = (s?: string) => { if (!s) return ""; const d = new Date(String(s).replace(" ", "T") + (/[zZ]|[+\-]\d\d:?\d\d$/.test(s || "") ? "" : "Z")); return isNaN(+d) ? esc(s) : d.toLocaleString("it-IT", { timeZone: "Europe/Rome", weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); };
const HAND: Record<string, string> = { RIGHT: "Destra", LEFT: "Sinistra" };
const SIDE: Record<string, string> = { BACKHAND: "Rovescio", FOREHAND: "Dritto" };
const MTYPE: Record<string, string> = { FRIENDLY: "Amichevole", COMPETITIVE: "Competitiva" };
const mapList = (arr: string[] | undefined, m: Record<string, string>) => (arr || []).map((x) => m[x] || x).join(", ");

function Avatar({ pic, size }: { pic?: string | null; size: number }) {
  if (pic) return <img src={pic} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" alt="" />;
  return <div style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }} className="rounded-full bg-slate-200 flex items-center justify-center shrink-0">👤</div>;
}

const Card = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    {title ? <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3> : null}
    {children}
  </div>
);
const Row = ({ k, v }: { k: string; v: React.ReactNode }) => v ? (
  <div className="flex items-center justify-between gap-3 py-0.5"><span className="text-slate-500 text-sm">{k}</span><b className="text-slate-900 text-sm text-right">{v}</b></div>
) : null;

const norm = (u: any): PlayerLite => {
  const src = u.user || u;
  return {
    id: String(u.user_id || u.id || (u.user && (u.user.user_id || u.user.id)) || ""),
    name: (src.full_name || src.name || [src.first_name, src.last_name].filter(Boolean).join(" ") || "").trim() || "giocatore " + (u.user_id || ""),
    pic: src.picture || src.avatar || src.image,
    gender: src.gender || src.sex, email: src.email, phone: src.phone,
  };
};

// ───────── grafico livello interattivo ─────────
function LevelChart({ series }: { series: { date: string; level: number }[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ x: number; y: number; level: number; date: string } | null>(null);
  if (series.length < 2)
    return <div className="text-sm text-slate-500">Servono almeno 2 partite per il grafico — usa “Carica altre 5” o “Vedi tutte”.</div>;
  const lv = series.map((s) => s.level), min = Math.min(...lv), max = Math.max(...lv);
  const pad = (max - min) * 0.12 || 0.1, lo = min - pad, hi = max + pad, W = 1000, H = 190, P = 6;
  const X = (i: number) => P + (i * (W - 2 * P)) / (series.length - 1);
  const Y = (v: number) => (H - P) - ((v - lo) / (hi - lo)) * (H - 2 * P);
  const line = series.map((s, i) => `${X(i).toFixed(1)},${Y(s.level).toFixed(1)}`).join(" ");
  const area = `${X(0).toFixed(1)},${H - P} ${line} ${X(series.length - 1).toFixed(1)},${H - P}`;
  const onMove = (e: React.MouseEvent) => {
    const box = ref.current!; const rect = box.getBoundingClientRect();
    let i = Math.round(((e.clientX - rect.left) / rect.width) * (series.length - 1));
    i = Math.max(0, Math.min(series.length - 1, i));
    setHover({ x: (i / (series.length - 1)) * 100, y: (Y(series[i].level) / H) * 100, level: series[i].level, date: series[i].date });
  };
  return (
    <div>
      <div ref={ref} className="relative cursor-crosshair" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full" style={{ height: 200 }}>
          <defs><linearGradient id="lvg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#16a34a" stopOpacity="0.25" /><stop offset="1" stopColor="#16a34a" stopOpacity="0" /></linearGradient></defs>
          <polygon points={area} fill="url(#lvg)" />
          <polyline points={line} fill="none" stroke="#16a34a" strokeWidth="2" />
        </svg>
        {hover && (<>
          <div className="absolute top-0 bottom-0 w-px bg-green-600/50 pointer-events-none" style={{ left: hover.x + "%" }} />
          <div className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-600 ring-4 ring-green-600/20 pointer-events-none" style={{ left: hover.x + "%", top: hover.y + "%" }} />
          <div className="absolute -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-center text-xs shadow pointer-events-none" style={{ left: Math.max(7, Math.min(93, hover.x)) + "%", top: hover.y + "%", transform: "translate(-50%,calc(-100% - 10px))" }}>
            <b className="text-green-700">Liv {fmtL(hover.level)}</b><br /><span className="text-slate-500">{fmtDay(hover.date)}</span>
          </div>
        </>)}
      </div>
      <div className="mt-1.5 text-xs text-slate-500">livello per partita · {series.length} punti · min <b>{fmtL(min)}</b> · max <b>{fmtL(max)}</b> · attuale <b>{fmtL(series[series.length - 1].level)}</b> — passa il mouse</div>
    </div>
  );
}

function MatchCard({ m, onOpen }: { m: any; onOpen: (uid: string) => void }) {
  const t0 = (m.teams || [])[0] || {}, t1 = (m.teams || [])[1] || {};
  const sets = (m.results || []).filter((s: any) => Array.isArray(s.scores) && s.scores.length >= 2);
  const played = sets.length > 0;
  const sc = (tid: string) => played ? sets.map((s: any) => { const e = (s.scores || []).find((x: any) => String(x.team_id) === String(tid)); return e ? e.score : "-"; }).join("  ") : "";
  const teamRow = (t: any) => (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      {(t.players || []).map((pl: any, i: number) => (
        <button key={i} onClick={() => onOpen(String(pl.user_id))} title={"apri profilo di " + (pl.name || "")} className="inline-flex items-center gap-1 hover:underline">
          <Avatar pic={pl.picture} size={22} />
          <span className="text-xs text-slate-700">{esc((pl.name || "").split(" ")[0])}{pl.level_value != null ? <span className="text-slate-400"> {Number(pl.level_value).toFixed(1).replace(".", ",")}</span> : null}</span>
        </button>
      ))}
    </div>
  );
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5">
      <div className="text-xs text-slate-500">{fmtDT(m.start_date)}{m.location ? " · " + m.location : ""}</div>
      <div className="mt-1.5 flex items-center justify-between gap-2">{teamRow(t0)}<b className="text-slate-900">{t0.team_result === "WON" ? "🏆 " : ""}{sc(t0.team_id)}</b></div>
      <div className="mt-1 flex items-center justify-between gap-2">{teamRow(t1)}<b className="text-slate-900">{t1.team_result === "WON" ? "🏆 " : ""}{sc(t1.team_id)}</b></div>
      {!played && <div className="mt-1 text-xs text-slate-400">Risultato non inserito</div>}
    </div>
  );
}

function Profile({ acct, p, onOpen }: { acct: string; p: PlayerLite; onOpen: (uid: string) => void }) {
  const [meta, setMeta] = useState<any>(null);
  const [contatti, setContatti] = useState<string>("loading"); // 'loading' | 'na' | rendered via state
  const [contRec, setContRec] = useState<any>(null);
  const [matches, setMatches] = useState<any[] | null>(null);
  const [size, setSize] = useState(5);
  const [seeAll, setSeeAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const tenantsCache = useRef<Record<string, { name: string; city: string }>>({});
  const [tenants, setTenants] = useState<Record<string, { name: string; city: string }>>({});

  // dati leggeri (host user) — niente Manager bloccante
  useEffect(() => {
    let alive = true;
    setMeta(null); setMatches(null); setSize(5); setSeeAll(false); setContRec(null); setContatti(p.email || p.phone ? "rec" : "loading");
    const get = <X,>(fn: () => Promise<X>) => fn().then((r) => r).catch(() => null);
    (async () => {
      const [social, lvl, stats, prefs, tops, mstats, playedWith] = await Promise.all([
        get(() => ptapp.call<any[]>(acct, "app", "v1/social/users", { params: { requester_user_id: "me", user_ids: p.id, exclude_followed: "false" } })),
        get(() => ptapp.call<any>(acct, "api", "v1/levels", { params: { user_id: p.id, sport_id: "PADEL" } })),
        get(() => ptapp.call<any>(acct, "app", "v1/social/users/" + p.id + "/stats")),
        get(() => ptapp.call<any>(acct, "app", "v1/profiles/" + p.id + "/sport_preferences/PADEL")),
        get(() => ptapp.call<any[]>(acct, "apiapp", "v1/matches_summary/top_tenants_by_player", { params: { player_user_id: p.id } })),
        get(() => ptapp.call<any>(acct, "apiapp", "v1/matches_summary/results", { params: { player_user_id: p.id, sport_id: "PADEL" } })),
        get(() => ptapp.call<any[]>(acct, "apiapp", "v1/relations/common_match_players", { params: { user_id: p.id, size: 12 } })),
      ]);
      if (!alive) return;
      let tops6 = (Array.isArray(tops) ? tops.slice() : []).sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 6);
      await Promise.all(tops6.map(async (t) => {
        const tid = t.tenant_id; if (tenantsCache.current[tid]) return;
        try { const tt = await ptapp.call<any>(acct, "apiapp", "v1/tenants/" + tid); tenantsCache.current[tid] = { name: (tt.tenant_name || tt.name || tid).trim(), city: (tt.address && (tt.address.city || tt.address.locality)) || "" }; }
        catch { tenantsCache.current[tid] = { name: tid.slice(0, 8), city: "" }; }
      }));
      if (!alive) return;
      setTenants({ ...tenantsCache.current });
      const r = (Array.isArray(social) && social[0]) ? social[0] : {};
      const levelArr = Array.isArray(lvl) ? lvl : (lvl ? [lvl] : []);
      setMeta({ r, level: (levelArr[0] || {}).level_value, stats, prefs, tops: tops6, mstats, playedWith: Array.isArray(playedWith) ? playedWith : [] });
    })();
    // contatti club (lazy, timeout via AbortController)
    const ctrl = new AbortController(); const to = setTimeout(() => ctrl.abort(), 7000);
    fetch(`/api/ptapp/call/moving/mgr/v2/users/${p.id}`, { headers: { "X-Authorization-Scope": "tenant:" + T }, signal: ctrl.signal })
      .then((r) => r.ok ? r.json() : null).then((rc) => { if (!alive) return; if (rc && rc.user_id) { setContRec(rc); setContatti("rec"); } else if (!(p.email || p.phone)) setContatti("na"); })
      .catch(() => { if (alive && !(p.email || p.phone)) setContatti("na"); })
      .finally(() => clearTimeout(to));
    return () => { alive = false; };
  }, [acct, p.id]);

  // carica partite (storico + grafico)
  useEffect(() => {
    let alive = true; setBusy(true);
    const nowISO = new Date().toISOString().slice(0, 19);
    ptapp.call<any[]>(acct, "apiapp", "v1/matches", { params: { user_id: p.id, to_start_date: nowISO, size, sort: "start_date,created_at,DESC" } })
      .then((d) => { if (alive) setMatches((Array.isArray(d) ? d : []).filter((m) => m.game_status === "PLAYED")); })
      .catch(() => { if (alive) setMatches([]); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [acct, p.id, size, seeAll]);

  const r = meta?.r || {};
  const name = (contRec?.full_name || r.full_name || p.name || "").trim() || "giocatore " + p.id;
  const pic = r.picture || contRec?.picture || p.pic;
  const gender = contRec?.gender || r.gender || p.gender;
  const eta = ageOf(contRec?.birth_date);
  const cityMain = meta?.tops?.length ? tenants[meta.tops[0].tenant_id]?.city : "";
  const myLevel = (m: any) => { for (const t of m.teams || []) for (const x of t.players || []) if (String(x.user_id) === String(p.id)) return x.level_value; return null; };
  const series = useMemo(() => (matches || []).map((m) => ({ date: m.start_date as string, level: myLevel(m) })).filter((s) => s.level != null && s.date).sort((a, b) => +new Date(a.date) - +new Date(b.date)), [matches]);

  const contRows = (rc: any) => (<>
    <Row k="Email" v={rc.email} /><Row k="Telefono" v={rc.phone} />
    <Row k="Lingua" v={rc.communications_language} /><Row k="Cliente dal" v={fmtDay(rc.created_at)} />
    {rc.accepts_commercial != null ? <Row k="Comunicazioni commerciali" v={rc.accepts_commercial ? "Sì" : "No"} /> : null}
  </>);

  return (
    <div className="space-y-4">
      {/* header */}
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Avatar pic={pic} size={64} />
            <div className="min-w-0">
              <div className="text-lg font-bold text-slate-900">{name}</div>
              <div className="text-sm text-slate-500">{[genderLabel(gender), eta ? eta + " anni" : "", cityMain ? "gioca a " + cityMain : ""].filter(Boolean).join(" · ")}</div>
              <div className="text-xs text-slate-400">uid {p.id}</div>
            </div>
          </div>
          {meta?.level != null && <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">{fmtLevel(meta.level)}</span>}
        </div>
      </Card>

      <Card title="Contatti">
        {contatti === "rec" || contRec ? contRows(contRec || p) : contatti === "na" ? <div className="text-sm text-slate-400">Non risulta cliente del club (nessun contatto).</div> : <div className="text-sm text-slate-400">carico…</div>}
      </Card>

      {meta?.mstats?.played != null && (
        <Card title="Statistiche partite">
          <div className="grid grid-cols-2 gap-x-8 sm:grid-cols-3">
            <Row k="Giocate" v={meta.mstats.played} /><Row k="Vinte" v={<span className="text-green-700">{meta.mstats.won}</span>} />
            <Row k="Perse" v={meta.mstats.lost} />{meta.mstats.drawn ? <Row k="Pareggi" v={meta.mstats.drawn} /> : null}
            <Row k="Efficacia" v={<span className="rounded-full bg-green-100 px-2 text-green-700">{meta.mstats.played ? Math.round((meta.mstats.won / meta.mstats.played) * 100) : 0}%</span>} />
          </div>
        </Card>
      )}

      <Card title="Andamento livello Playtomic">
        {matches == null ? <div className="text-sm text-slate-400">carico…</div> : <LevelChart series={series} />}
      </Card>

      {meta?.stats && (
        <Card title="Statistiche social">
          <Row k="Follower" v={meta.stats.followers} /><Row k="Following" v={meta.stats.following} />
          {meta.stats.is_followed != null ? <Row k="Ti segue" v={meta.stats.is_follower ? "Sì" : "No"} /> : null}
        </Card>
      )}

      {meta?.prefs && (
        <Card title="Preferenze di gioco">
          <Row k="Mano preferita" v={mapList(meta.prefs.best_hand, HAND)} />
          <Row k="Posizione" v={meta.prefs.court_side && meta.prefs.court_side.length === 2 ? "Entrambi i lati" : mapList(meta.prefs.court_side, SIDE)} />
          <Row k="Tipo di partita" v={mapList(meta.prefs.type_of_match, MTYPE)} />
        </Card>
      )}

      {!!meta?.tops?.length && (
        <Card title="Club dove gioca">
          {meta.tops.map((t: any) => (
            <div key={t.tenant_id} className="flex items-center justify-between py-0.5">
              <span className="text-sm text-slate-700">{tenants[t.tenant_id]?.name || t.tenant_id}{tenants[t.tenant_id]?.city ? <span className="text-slate-400"> ({tenants[t.tenant_id].city})</span> : null}</span>
              <span className="rounded-full bg-slate-100 px-2 text-xs text-slate-600">{t.count} partite</span>
            </div>
          ))}
        </Card>
      )}

      <Card title={`Storico partite (${matches ? matches.length : 0}${seeAll ? "" : "+"})`}>
        {matches == null ? <div className="text-sm text-slate-400">carico…</div> : (
          <>
            <div className="grid gap-2.5" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))" }}>
              {matches.map((m) => <MatchCard key={m.match_id} m={m} onOpen={onOpen} />)}
              {matches.length === 0 && <div className="text-sm text-slate-400">nessuna partita giocata</div>}
            </div>
            {!seeAll && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button disabled={busy} onClick={() => setSize((s) => s + 5)} className="flex-1 min-w-[160px] rounded-md bg-green-600 px-3 py-2.5 font-semibold text-white hover:bg-green-700 disabled:opacity-50">➕ Carica altre 5 partite</button>
                <button disabled={busy} onClick={() => { setSeeAll(true); setSize(500); }} className="flex-1 min-w-[140px] rounded-md border border-slate-300 px-3 py-2.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">Vedi tutte le partite</button>
              </div>
            )}
          </>
        )}
      </Card>

      {!!meta?.playedWith?.length && (
        <Card title="Gioca spesso con">
          <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))" }}>
            {meta.playedWith.slice(0, 12).map((u: any) => (
              <button key={u.user_id} onClick={() => onOpen(String(u.user_id))} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-left hover:bg-slate-50">
                <Avatar pic={u.picture} size={34} />
                <div className="min-w-0"><div className="truncate text-sm font-semibold text-slate-800">{esc((u.full_name || "").trim())}</div><div className="text-xs text-slate-400">uid {u.user_id}</div></div>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default function Players() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [acct, setAcct] = useState("staff");
  const [mode, setMode] = useState<Mode>("club");
  const [q, setQ] = useState("");
  const [results, setResults] = useState<PlayerLite[] | null>(null);
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState<PlayerLite | null>(null);

  useEffect(() => { ptapp.accounts().then((a) => { setAccounts(a); if (a.length && !a.find((x) => x.key === acct)) setAcct(a[0].key); }).catch(() => {}); }, []);

  const search = async () => {
    const query = q.trim();
    if (!query) { setStatus("scrivi qualcosa"); return; }
    setStatus("cerco…"); setResults(null); setSel(null);
    try {
      let list: any[];
      if (mode === "club") {
        const words = query.split(/\s+/).filter(Boolean);
        let pivot = query;
        if (words.length > 1) { pivot = words[0]; for (const w of words) if (w.length >= pivot.length) pivot = w; }
        const r = await fetch(`/api/ptapp/call/moving/mgr/v2/users/suggestions/players?tenant_id=${T}&size=50&filter=${encodeURIComponent(pivot)}`, { headers: { "X-Authorization-Scope": "tenant:" + T } });
        let data = r.ok ? await r.json() : [];
        list = Array.isArray(data) ? data : data.items || data.users || [];
        if (words.length > 1) { const ws = words.map((w) => w.toLowerCase()); const nrm = (s: string) => (s || "").toLowerCase().replace(/\s+/g, " ").trim(); list = list.filter((u) => { const n = nrm(u.full_name); return ws.every((w) => n.includes(w)); }); }
      } else {
        const data = await ptapp.call<any[]>(acct, "app", "v1/social/users", { params: { name: query, requester_user_id: "me", size: 50 } });
        list = Array.isArray(data) ? data : [];
      }
      const players = list.map(norm).filter((p) => p.id);
      setResults(players); setStatus(players.length ? "" : "nessun giocatore trovato");
    } catch (e: any) { setStatus("Errore: " + e.message); setResults([]); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-bold text-slate-900">Ricerca giocatori</h2>
        <div className="flex flex-wrap items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="nome, oppure email/telefono nel club…" className="flex-1 min-w-[220px] rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <select value={mode} onChange={(e) => setMode(e.target.value as Mode)} className="rounded-md border border-slate-300 px-2 py-2 text-sm">
            <option value="club">Club (nome/email/tel)</option>
            <option value="global">Tutti (globale)</option>
          </select>
          {mode === "global" && (
            <select value={acct} onChange={(e) => setAcct(e.target.value)} className="rounded-md border border-slate-300 px-2 py-2 text-sm" title="account">
              {accounts.map((a) => <option key={a.key} value={a.key}>{a.email}</option>)}
            </select>
          )}
          <button onClick={search} className="rounded-md bg-moving-500 px-4 py-2 text-sm font-semibold text-white hover:opacity-90">Cerca</button>
        </div>
        <div className="mt-3">
          {results == null ? <div className="text-sm text-slate-500">{status || "scrivi un nome (o email/telefono) e premi Cerca"}</div> :
            results.length === 0 ? <div className="text-sm text-slate-500">{status}</div> : (
              <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))" }}>
                {results.map((p) => (
                  <button key={p.id} onClick={() => setSel(p)} className="flex items-center gap-3 rounded-lg border border-slate-200 p-2.5 text-left hover:bg-slate-50">
                    <Avatar pic={p.pic} size={42} />
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-800">{p.name}</div>
                      <div className="text-xs text-slate-500">{genderLabel(p.gender)} · uid {p.id}</div>
                      {mode === "club" && (p.email || p.phone) ? <div className="truncate text-xs text-slate-400">{p.email}{p.email && p.phone ? " · " : ""}{p.phone}</div> : null}
                    </div>
                  </button>
                ))}
              </div>
            )}
        </div>
      </div>

      {sel && <Profile acct={acct} p={sel} onOpen={(uid) => setSel({ id: uid, name: "", pic: null })} />}
    </div>
  );
}
